import React from "react";
import {
  WhmBox,
  WhmTypography,
  WhmDatePicker,
  WhmLocalizationProvider,
  WhmTextField,
} from "@securitize/reactjs-whm";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { DateRange } from "../../../data/types/portfolio-statement/portfolio-statement";
import { translationKeys, useTranslation } from "../../../hooks/useTranslation";
import { Translate } from "../../shared/translation";

const DayjsDatePicker = WhmDatePicker<Dayjs, Dayjs>;

interface DateRangeSectionProps {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  tokenName?: string;
  isDesktop?: boolean;
}

export const DateRangeSection: React.FC<DateRangeSectionProps> = ({
  dateRange,
  onDateRangeChange,
  tokenName,
  isDesktop = false,
}) => {
  const { getTranslation: t } = useTranslation();

  const handleStartDateChange = (date: Dayjs | null) => {
    onDateRangeChange({ ...dateRange, startDate: date });
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    onDateRangeChange({ ...dateRange, endDate: date });
  };

  return (
    <WhmBox sx={{ mb: 3 }}>
      <WhmTypography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        <Translate
          translationKey={translationKeys.PORTFOLIO_STATEMENT_DATE_RANGE_TITLE}
        />
      </WhmTypography>

      <WhmTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(translationKeys.PORTFOLIO_STATEMENT_DATE_RANGE_SUBTITLE, {
          tokenName: tokenName || "your asset",
        })}
      </WhmTypography>

      <WhmLocalizationProvider dateAdapter={AdapterDayjs}>
        <WhmBox
          sx={{
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            alignItems: isDesktop ? "center" : "stretch",
            gap: isDesktop ? 1 : 2,
          }}
        >
          <WhmBox sx={{ flex: isDesktop ? 1 : undefined, minWidth: 0 }}>
            <DayjsDatePicker
              label={t(translationKeys.PORTFOLIO_STATEMENT_STARTING_DATE)}
              value={dateRange.startDate}
              onChange={handleStartDateChange}
              maxDate={dateRange.endDate || dayjs()}
              inputFormat="MM/DD/YYYY"
              renderInput={(params) => (
                <WhmTextField
                  {...params}
                  fullWidth
                  size="small"
                  helperText="'MM/DD/YYYY', Inception Date"
                />
              )}
            />
          </WhmBox>

          {isDesktop && (
            <WhmTypography
              variant="body1"
              color="text.secondary"
              sx={{ flexShrink: 0, mt: -2.5 }}
            >
              -
            </WhmTypography>
          )}

          <WhmBox sx={{ flex: isDesktop ? 1 : undefined, minWidth: 0 }}>
            <DayjsDatePicker
              label={t(translationKeys.PORTFOLIO_STATEMENT_ENDING_DATE)}
              value={dateRange.endDate}
              onChange={handleEndDateChange}
              minDate={dateRange.startDate || undefined}
              maxDate={dayjs()}
              inputFormat="MM/DD/YYYY"
              renderInput={(params) => (
                <WhmTextField
                  {...params}
                  fullWidth
                  size="small"
                  helperText="MM/DD/YYYY"
                />
              )}
            />
          </WhmBox>
        </WhmBox>
      </WhmLocalizationProvider>
    </WhmBox>
  );
};
